import { z } from 'zod'
import { ProductSchema } from '@/schemas/catalog/product'
import { ZIP_CODE_REGEX, ZipCodeSchema } from '@/schemas/customer/address'
import { MoneySchema } from '@/schemas/shared/money'
import { OptionalShippingBySellerSchema, ShippingQuotesResponseSchema } from '@/schemas/shared/shipping'
import { OptionalTextSchema } from '@/schemas/shared/text'
import { tolerantArray } from '@/schemas/shared/tolerantArray'

/**
 * Schemas do carrinho (loja). Contrato da API (Glofi.API/Controllers/CartController.cs e
 * `CartService.ParseCartResponse`), travado em tests/Glofi.API.Tests/Snapshots/Cart/*.shape.txt:
 *
 * | Rota | Verbo | Corpo | Resposta 200 |
 * | --- | --- | --- | --- |
 * | `cart/current` | GET | - | `{ cart: ICart \| null }` (não cria carrinho) |
 * | `cart/add-product` | POST | `{ productId, quantity }` | `ICart` (cria o carrinho se não houver) |
 * | `cart/update-product` | POST | `{ productId, quantity }` | `ICart` |
 * | `cart/remove-product` | POST | `{ productId, quantity }` | `ICart` |
 * | `cart/calc-shipping-maestro` | POST | `{ zipcode }` | `{ cart, shipmentResponse: { quotes }, shipmentBySeller? }` |
 * | `cart/select-shipping` | POST | `{ sellerId, offerCode }` | `{ cart, shipmentBySeller }` (só na staging/api) |
 *
 * Erros: 409 `cartNotFound` com `cart` (todo endpoint que precisa de carrinho aberto e não encontra); 400 `stockError`
 * com `available` (add e update); 404 `productNotFound` (add) e 400 `productNotFound` (update); 400 com só `message`
 * para produto repetido no add ("Você já adicionou este produto ao carrinho!") e CEP inválido no frete; 422 com só
 * `message` no select-shipping (a opção saiu da cotação entre a tela e o clique).
 *
 * O produto do item é uma projeção do produto do catálogo (`ProductSchema`), não um schema próprio: a projeção
 * "simple" manda id, nome, preços, sku, estoque, tamanho, slug e miniatura; o remove-coupon, o update-coupon, o login e
 * o checkout mandam também categorias, descrição, SEO e tags, sem sku.
 *
 * Fonte única do carrinho: a sessão do login (`AuthSessionSchema.cart`) e o pedido do checkout (`order.cart`) usam o
 * `CartSchema` (mesmo CartDTO na API).
 */

const text = OptionalTextSchema

const orNull = <T extends z.ZodType>(schema: T) =>
    schema.nullish().transform((value) => (value ?? null) as z.output<T> | null)

// ===== PRODUTO E ITEM =====

/**
 * Produto embutido no item do carrinho. Estritos: `id`, preços, estoque e quantidade. O `slug` é `null` quando a cópia
 * do produto no carrinho não tem (cópias antigas: 4406 carrinhos abertos na produção em 10-2026); a tela mostra o item
 * sem link para a PDP.
 */
export const CartProductSchema = ProductSchema.pick({
    id: true,
    name: true,
    slug: true,
    price: true,
    discountedPrice: true,
    stockQuantity: true,
    shortDescription: true,
    thumbnailUrl: true,
    imagesURL: true,
    images: true,
    sku: true,
    order: true,
    productSize: true,
    type: true,
    visible: true,
    sellerName: true,
    shippingText: true,
    categories: true,
    tags: true,
    group: true,
    fullDescription: true,
    seoTitle: true,
    seoDescription: true,
}).extend({
    slug: z
        .string()
        .nullish()
        .transform((value) => value?.trim() || null),
})

/** Cupom no carrinho: a projeção "simple" manda só o nome e o código. */
export const CartDiscountAppliedSchema = z.looseObject({
    discountCode: z.string(),
    name: text,
})

/** Desconto de produto no item (cupom de escopo Product): o DiscountAppliedDTO inteiro. */
export const CartItemDiscountAppliedSchema = z.looseObject({
    discountCode: text,
    name: text,
    value: MoneySchema.nullish(),
    scopeType: text,
    calculationType: text,
})

/** Item do carrinho. A API não manda `id` do item: a chave é o `product.id`. */
export const CartItemSchema = z.object({
    product: CartProductSchema,
    quantity: z.number().int(),
    discountApplied: orNull(CartItemDiscountAppliedSchema),
    freightId: orNull(z.string()),
})

// ===== CARRINHO =====

export const CartSchema = z.object({
    id: z.string(),
    /** Ausente no carrinho que o remove-coupon e o update-coupon montam à mão. */
    sessionId: orNull(z.string()),
    /** CEP do último frete calculado, só dígitos; `''` antes do primeiro cálculo. */
    cep: text,
    /** Item sem produto ou com a cópia do produto ilegível sai da lista (e é registrado); os outros ficam. */
    cartItems: tolerantArray(CartItemSchema, 'itens do carrinho'),
    discountApplied: orNull(CartDiscountAppliedSchema),
    /** Desconto do cupom. */
    discount: MoneySchema,
    /** Frete da primeira cotação do último cálculo. */
    shippingTax: MoneySchema,
    subTotal: MoneySchema,
    totalWithoutTax: MoneySchema,
    total: MoneySchema,
})

// ===== RESPOSTAS =====

/** `GET cart/current`: o carrinho aberto da sessão ou do cliente, ou `null`. */
export const CurrentCartResponseSchema = z.object({
    cart: CartSchema.nullable(),
})

/**
 * `POST cart/calc-shipping-maestro`. Na production/api, a API grava no carrinho o preço e o prazo da primeira cotação e
 * as outras são só informativas. Na staging/api, `shipmentResponse` traz uma cotação agregada ("Frete total") e
 * `shipmentBySeller` o detalhamento por vendedor, com a opção valendo de cada um (`null` quando a API não manda).
 * Cotação de `schemas/shared/shipping.ts`, a mesma da PDP.
 */
export const CartShippingResponseSchema = ShippingQuotesResponseSchema.extend({
    cart: CartSchema,
    shipmentBySeller: OptionalShippingBySellerSchema,
})

/** `POST cart/select-shipping` (staging/api): o carrinho com o frete novo e o detalhamento por vendedor recalculado. */
export const SelectCartShippingResponseSchema = z.object({
    cart: CartSchema,
    shipmentBySeller: OptionalShippingBySellerSchema,
})

// ===== CORPOS DE ERRO (lidos de `ApiError.data`) =====

/**
 * 409 `cartNotFound`: a sessão ou o cliente não tem carrinho aberto (virou pedido ou expirou). A loja troca o
 * carrinho guardado pelo `cart` da resposta (hoje sempre null) em vez de mostrar erro de CEP ou de cupom.
 */
export const CartNotFoundBodySchema = z.object({
    statusError: z.literal('cartNotFound'),
    message: text,
    cart: orNull(CartSchema),
})

/** 400 `stockError` do add-product e do update-product: `available` é o estoque atual do produto. */
export const StockErrorBodySchema = z.object({
    statusError: z.literal('stockError'),
    message: text,
    available: z.number().nullish(),
})

/** Produto do `stockChanges` do `outStockError`. */
export const StockChangeSchema = z.object({
    prodId: text,
    stockOk: z.boolean(),
    remainingQty: z.number(),
    message: text,
})

/** 409 `outStockError` do checkout/finish: algum item do carrinho ficou sem estoque. */
export const OutStockErrorBodySchema = z.object({
    statusError: z.literal('outStockError'),
    message: text,
    stockChanges: z
        .array(StockChangeSchema)
        .nullish()
        .transform((value) => value ?? []),
})

// ===== REQUISIÇÕES =====

/** `cart/add-product`. A API troca quantidade menor ou igual a zero por 1; o front já manda 1. */
export const AddCartItemRequestSchema = z.object({
    productId: z.string().min(1),
    quantity: z.number().int().min(1).default(1),
})

/** `cart/update-product`: a quantidade nova do item. */
export const UpdateCartItemRequestSchema = z.object({
    productId: z.string().min(1),
    quantity: z.number().int().min(1),
})

/** `cart/remove-product`. A quantidade só vai para a mensagem da auditoria; o item sai inteiro. */
export const RemoveCartItemRequestSchema = z.object({
    productId: z.string().min(1),
    quantity: z.number().int().min(0).default(0),
})

/**
 * `cart/calc-shipping-maestro`: CEP pela regra única do endereço, só com dígitos (a API tira hífen, barra e ponto,
 * mas não valida o formato).
 */
export const CalcCartShippingRequestSchema = z.object({
    zipcode: ZipCodeSchema(),
})

// ===== FORMULÁRIO =====

/** Formulário "Calcular frete" do carrinho (regra única do CEP, `ZIP_CODE_REGEX` do customer). */
export const CartShippingFormSchema = z.object({
    zipCode: z.string().regex(ZIP_CODE_REGEX, 'CEP inválido'),
})

// ===== TIPOS =====

export type ICartProduct = z.infer<typeof CartProductSchema>
export type ICartDiscountApplied = z.infer<typeof CartDiscountAppliedSchema>
export type ICartItemDiscountApplied = z.infer<typeof CartItemDiscountAppliedSchema>
export type ICartItem = z.infer<typeof CartItemSchema>
export type ICart = z.infer<typeof CartSchema>
export type ICurrentCartResponse = z.infer<typeof CurrentCartResponseSchema>
export type ICartShippingResponse = z.infer<typeof CartShippingResponseSchema>
export type ISelectCartShippingResponse = z.infer<typeof SelectCartShippingResponseSchema>
export type ICartNotFoundBody = z.infer<typeof CartNotFoundBodySchema>
export type IStockErrorBody = z.infer<typeof StockErrorBodySchema>
export type IStockChange = z.infer<typeof StockChangeSchema>
export type IOutStockErrorBody = z.infer<typeof OutStockErrorBodySchema>
export type IAddCartItemRequest = z.input<typeof AddCartItemRequestSchema>
export type IUpdateCartItemRequest = z.infer<typeof UpdateCartItemRequestSchema>
export type IRemoveCartItemRequest = z.input<typeof RemoveCartItemRequestSchema>
export type ICartShippingForm = z.infer<typeof CartShippingFormSchema>
